import React from 'react';
import {getAwpApiBase} from '../../../utils/awpApiBase';
import {getAwpClientHeaders} from '../../../session/awpRequestHeaders';
import {normalizePropertyItemsForRead} from '../../../utils/entityProperties';

async function fetchEntityProperties(entityId) {
    const res = await fetch(
        `${getAwpApiBase()}/api/entities/${encodeURIComponent(entityId)}/properties`,
        {headers: getAwpClientHeaders()},
    );
    if (res.status === 404) return [];
    if (!res.ok) return [];
    const data = await res.json();
    return normalizePropertyItemsForRead(data?.items || []);
}

function propertyKeyForItem(item) {
    if (typeof item?.propertyId === 'string' && item.propertyId.trim()) {
        return item.propertyId.trim();
    }
    if (typeof item?.propertyLabel === 'string' && item.propertyLabel.trim()) {
        return item.propertyLabel.trim();
    }
    return null;
}

function propertyLabelForItem(item, propertyKey) {
    if (typeof item?.propertyLabel === 'string' && item.propertyLabel.trim()) {
        return item.propertyLabel.trim();
    }
    return propertyKey;
}

/**
 * Fetch and union KB properties for all entities currently on the chart.
 *
 * @param {Array<{ wikidataId: string }>} chartEntities
 * @returns {{
 *   propertiesByEntityId: Record<string, Array<object>>,
 *   propertyOptions: Array<{ propertyKey: string, label: string }>,
 *   loading: boolean,
 * }}
 */
export function useChartEntityProperties(chartEntities, {enabled = true} = {}) {
    const [propertiesByEntityId, setPropertiesByEntityId] = React.useState({});
    const [loading, setLoading] = React.useState(false);

    const entityIdsKey = React.useMemo(
        () => chartEntities.map((entity) => entity.wikidataId).filter(Boolean).sort().join('\0'),
        [chartEntities],
    );

    React.useEffect(() => {
        if (!enabled || !entityIdsKey) {
            if (!enabled) {
                setPropertiesByEntityId({});
            }
            setLoading(false);
            return undefined;
        }

        let cancelled = false;
        setLoading(true);

        const entityIds = entityIdsKey.split('\0').filter(Boolean);

        Promise.all(entityIds.map(async (entityId) => {
            try {
                const items = await fetchEntityProperties(entityId);
                return [entityId, items];
            } catch {
                return [entityId, []];
            }
        }))
            .then((entries) => {
                if (cancelled) return;

                const next = {};
                for (const [entityId, items] of entries) {
                    next[entityId] = items;
                }
                setPropertiesByEntityId(next);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [enabled, entityIdsKey]);

    const propertyOptions = React.useMemo(() => {
        const byKey = new Map();

        for (const items of Object.values(propertiesByEntityId)) {
            for (const item of items || []) {
                const propertyKey = propertyKeyForItem(item);
                if (!propertyKey) continue;
                if (byKey.has(propertyKey)) continue;
                byKey.set(propertyKey, {
                    propertyKey,
                    label: propertyLabelForItem(item, propertyKey),
                });
            }
        }

        return Array.from(byKey.values()).sort((a, b) => (
            a.label.localeCompare(b.label, undefined, {sensitivity: 'base'})
        ));
    }, [propertiesByEntityId]);

    return {propertiesByEntityId, propertyOptions, loading};
}
